import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';
import PhoneFormat from 'components/ui/text-formats/PhoneFormat';
import moment from 'moment';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

// Shartnoma tuzishda mijoz raqami bo'yicha topilgan CRM lidning ichki
// ma'lumotlarini ko'rsatadi (mas'ul xodim/vositachi, bosqich, manba, izohlar).
const LeadInfoModal = ({ open, onClose, lead }) => {
  const { t } = useTranslation();

  if (!lead) return null;

  const stage = lead?.crm_block_item;
  const board = stage?.crm_block;
  const contacts = lead?.contacts || [];
  const fields = (lead?.fields || []).filter(
    (f) => f?.value !== null && f?.value !== '',
  );
  const comments = lead?.comments || [];

  const Row = ({ label, children }) =>
    children ? (
      <div className='flex justify-between gap-4 py-1.5 border-b border-dashed border-gray-200'>
        <span className='text-gray-500 text-sm'>{label}</span>
        <span className='text-sm font-medium text-right'>{children}</span>
      </div>
    ) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ className: '!base-border !shadow-none' }}
    >
      <DialogTitle className='!bg-gray-100 !mb-2 flex items-center justify-between'>
        <span className='flex items-center gap-2'>
          <i className='bi bi-person-lines-fill text-base-color' />
          {t('contract.step.one.lead.title', 'CRM lid topildi')}
        </span>
        <button
          type='button'
          onClick={onClose}
          className='text-gray-400 hover:text-gray-600'
        >
          <i className='bi bi-x-lg' />
        </button>
      </DialogTitle>

      <DialogContent>
        {/* Mas'ul xodim (vositachi) — asosiy urg'u */}
        <div className='rounded-lg bg-base-color-light/10 border border-base-color/30 p-3 mb-3'>
          <div className='text-xs text-gray-500'>
            {t('contract.step.one.lead.responsible', "Mas'ul xodim (vositachi)")}
          </div>
          <div className='text-lg font-semibold text-base-color flex items-center gap-2'>
            <i className='bi bi-person-badge' />
            {lead?.user?.name ||
              t('contract.step.one.lead.noResponsible', 'Belgilanmagan')}
          </div>
        </div>

        <div className='px-1'>
          <Row label={t('contract.step.one.lead.name', 'Lid nomi')}>
            {lead?.name}
          </Row>
          <Row label={t('contract.step.one.lead.board', 'Voronka')}>
            {board?.name}
          </Row>
          <Row label={t('contract.step.one.lead.stage', 'Bosqich')}>
            {stage?.name && (
              <span
                className='inline-flex items-center gap-1 px-2 py-0.5 rounded text-white text-xs'
                style={{ background: stage?.color || '#0057FF' }}
              >
                {stage.name}
              </span>
            )}
          </Row>
          <Row label={t('contract.step.one.lead.budget', 'Byudjet')}>
            {lead?.budget ? <CurrencyFormat value={lead.budget} /> : null}
          </Row>
          <Row label={t('contract.step.one.lead.createdAt', 'Yaratilgan')}>
            {lead?.created_at
              ? moment(lead.created_at).format('DD.MM.YYYY HH:mm')
              : null}
          </Row>

          {/* Manba va boshqa maxsus maydonlar */}
          {fields.map((f) => (
            <Row key={f.id} label={f?.field?.name || t('common.field', 'Maydon')}>
              {String(f.value)}
            </Row>
          ))}
        </div>

        {/* Kontaktlar */}
        {contacts.length > 0 && (
          <div className='mt-3'>
            <div className='text-sm font-semibold text-gray-600 mb-1'>
              {t('contract.step.one.lead.contacts', 'Kontaktlar')}
            </div>
            {contacts.map((c) => (
              <div
                key={c.id}
                className='flex justify-between items-center py-1 text-sm'
              >
                <span>{c?.name || '—'}</span>
                <span className='flex flex-col items-end text-gray-600'>
                  {(c?.phones || []).map((p) => (
                    <PhoneFormat key={p.id} value={p.phone} />
                  ))}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Izohlar */}
        {comments.length > 0 && (
          <div className='mt-3'>
            <div className='text-sm font-semibold text-gray-600 mb-1'>
              {t('contract.step.one.lead.comments', 'Izohlar')}
            </div>
            <div className='max-h-40 overflow-y-auto flex flex-col gap-1'>
              {comments.map((cm) => (
                <div
                  key={cm.id}
                  className='bg-gray-50 rounded p-2 text-sm border border-gray-100'
                >
                  <div className='flex justify-between text-xs text-gray-400'>
                    <span>{cm?.name}</span>
                    <span>
                      {cm?.created_at
                        ? moment(cm.created_at).format('DD.MM.YYYY HH:mm')
                        : ''}
                    </span>
                  </div>
                  <div>{cm?.comment || cm?.text || cm?.body}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default memo(LeadInfoModal);
